import { Modal } from './Modal';
import { Button } from './Button';

interface DependencyDeleteDialogProps {
    /** Whether the dialog is open */
    isOpen: boolean;
    /** Callback when dialog is closed without confirming */
    onClose: () => void;
    /** Callback when user confirms deletion despite dependencies */
    onConfirm: () => void;
    /** Name of the task being deleted */
    taskName: string;
    /** Tasks that depend on the task being deleted */
    dependentTasks: { id: number; name: string }[];
    /** Whether the confirm action is in progress */
    isLoading?: boolean;
}

/**
 * Dialog for deleting a task that has dependents.
 * Shows the list of tasks that will lose their dependency and lets the user confirm or cancel.
 */
export const DependencyDeleteDialog = ({
    isOpen,
    onClose,
    onConfirm,
    taskName,
    dependentTasks,
    isLoading = false,
}: DependencyDeleteDialogProps) => {
    const handleClose = isLoading ? () => {} : onClose;

    return (
        <Modal isOpen={isOpen} onClose={handleClose} title="Delete Task with Dependents">
            <div className="space-y-4">
                <p className="text-gray-700">
                    Deleting &ldquo;{taskName}&rdquo; will remove {dependentTasks.length} task{' '}
                    {dependentTasks.length === 1 ? 'dependency' : 'dependencies'}. These tasks will
                    lose their dependency on it:
                </p>

                <ul className="list-disc list-inside space-y-1 text-gray-700 bg-gray-50 rounded-md p-3">
                    {dependentTasks.map((t) => (
                        <li key={t.id} className="text-sm">
                            {t.name}
                        </li>
                    ))}
                </ul>

                <p className="text-sm text-gray-600">This action cannot be undone.</p>

                <div className="flex justify-end gap-3 pt-4">
                    <Button onClick={handleClose} variant="secondary" disabled={isLoading}>
                        Cancel
                    </Button>
                    <Button
                        onClick={onConfirm}
                        variant="danger"
                        disabled={isLoading}
                        aria-busy={isLoading}
                    >
                        {isLoading ? 'Processing...' : 'Delete Anyway'}
                    </Button>
                </div>
            </div>
        </Modal>
    );
};
